// Shared PDF field-drawing loop.
//
// The one implementation used by both fill paths: the server sidecar
// (priv/pdf/compose.mjs, multi-signer envelopes) and the browser hook
// (assets/js/hooks/pdf_form_filler.js, single-signer fills). Keeping it in
// one place is what makes the two paths produce identical pages.
//
// Deliberately imports nothing — pdf-lib lives in two vendored copies
// (priv/pdf/pdf-lib.mjs and assets/vendor/pdf-lib.mjs) and base64 decoding
// differs between Node and the browser, so callers inject both.

/**
 * Draws resolved field placements onto an already-loaded PDF document.
 *
 * @param pdfDoc  a loaded pdf-lib PDFDocument
 * @param fields  [{type, page, x, y, w, h, value}] — `value` is the text,
 *                "true" for a checked checkbox, or a PNG data URL for
 *                signature/initials fields. Coordinates are PDF points with
 *                a top-left origin.
 * @param deps    {font, rgb, toBytes} — an embedded font, pdf-lib's rgb, and
 *                a base64 → bytes decoder for the host environment.
 */
export async function drawFields(pdfDoc, fields, { font, rgb, toBytes }) {
  const pages = pdfDoc.getPages()
  const black = rgb(0, 0, 0)

  for (const field of fields) {
    const value = field.value || ""
    if (!value || value === "false") continue

    const pageIndex = (field.page || 1) - 1
    if (pageIndex >= pages.length) continue
    const page = pages[pageIndex]

    // Convert from top-left origin to pdf-lib bottom-left origin
    const pdfY = page.getHeight() - field.y - field.h

    if (field.type === "checkbox") {
      if (value === "true") {
        // Helvetica (WinAnsi) cannot encode "✓", so draw the check as two
        // strokes centered in the field box.
        const s = Math.min(field.w, field.h) * 0.6
        const cx = field.x + field.w / 2
        const cy = pdfY + field.h / 2
        const thickness = Math.max(1.2, s * 0.14)
        page.drawLine({
          start: { x: cx - s / 2, y: cy },
          end: { x: cx - s / 8, y: cy - s / 3 },
          thickness,
          color: black,
        })
        page.drawLine({
          start: { x: cx - s / 8, y: cy - s / 3 },
          end: { x: cx + s / 2, y: cy + s / 3 },
          thickness,
          color: black,
        })
      }
    } else if (field.type === "signature" || field.type === "initials") {
      if (value.startsWith("data:image/png")) {
        const pngImage = await pdfDoc.embedPng(toBytes(value.split(",")[1]))

        // Scale to fit the field box while preserving aspect ratio
        const imgDims = pngImage.scale(1)
        const s = Math.min(field.w / imgDims.width, field.h / imgDims.height)
        const drawW = imgDims.width * s
        const drawH = imgDims.height * s

        page.drawImage(pngImage, {
          x: field.x + (field.w - drawW) / 2,
          y: pdfY + (field.h - drawH) / 2,
          width: drawW,
          height: drawH,
        })
      }
    } else {
      const fontSize = Math.min(field.h * 0.65, 14)
      page.drawText(field.type === "date" ? formatDateValue(value) : value, {
        x: field.x + 4,
        // Center the glyph body (~0.72em cap height) in the box rather than
        // the full em square, so text doesn't sit on the box bottom.
        y: pdfY + (field.h - fontSize * 0.72) / 2,
        size: fontSize,
        font,
        color: black,
      })
    }
  }
}

// ISO date (from native date inputs) → MM/DD/YYYY for the printed document.
export function formatDateValue(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  return m ? `${m[2]}/${m[3]}/${m[1]}` : value
}
